import { computed, ref, watch } from 'vue';
import { useField } from 'vee-validate';
import { useInputMask } from '../../../composables/use-input-mask.composable';
import { getYupValidationSchema } from '../validations-schema';

const DEFAULT_REQUIRED_MESSAGE = 'Feltet er påkrævet';

export default {
    props: {
        attributesString: {
            type: String,
            default: ''
        },
        autoComplete: {
            type: String,
            default: 'off'
        },
        backgroundColor: {
            type: String,
            default: ''
        },
        description: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'text'
        },
        maxLength: {
            type: Number
        },
        clearButtonAriaLabel: {
            type: String,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        predefinedValue: {
            type: String,
            default: ''
        },
        format: {
            type: Object
        },
        emitUnmaskedValue: {
            type: Boolean,
            default: false,
        },
        inputMode: {
            type: String
        },
        prefix: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false
        },
        validators: {
            type: Array
        },
        value: {
            type: String
        }
    },
    setup(props, { emit }) {
        const { inputElement, updateMaskedValue, updateMaskFormat, getMaskedValue, hasMask } = useInputMask(
            props.format,
            (mask) => {
                if (props.emitUnmaskedValue) {
                    emit('input', mask.value === '' ? '' : mask.typedValue);
                }
            }
        );

        watch(() => props.format,
            (newFormat) => updateMaskFormat(newFormat),
            { deep: true }
        );

        const validators = (props.validators || []).map(validator => {
            if (validator.type !== 'RequiredValidator') {
                return validator;
            }

            return {
                ...validator,
                model: {
                    ...validator.model,
                    message: validator.model?.message || DEFAULT_REQUIRED_MESSAGE,
                },
            };
        });
        const hasRequiredValidator = validators.some(validator => validator.type === 'RequiredValidator');
        const usesRequiredPropValidator = props.required && !hasRequiredValidator;
        if (usesRequiredPropValidator) {
            validators.push({
                type: 'RequiredValidator',
                model: { message: DEFAULT_REQUIRED_MESSAGE },
            });
        }

        const { value, errorMessage, handleBlur, handleChange, validate, meta } = useField(() => props.name,
            validators.length ? getYupValidationSchema(validators) : undefined, {
            initialValue: props.predefinedValue,
            syncVModel: true,
            validateOnValueUpdate: false,
        });

        const hasValidationError = computed(() => !!errorMessage.value || (meta.validated && !meta.valid));

        watch(value, (newValue, oldValue) => {
            if (newValue !== oldValue && !hasMask.value) {
                // Only manually update input value when there's no mask
                // IMask handles the display value and cursor position automatically
                requestAnimationFrame(() => {
                    if (inputElement.value) {
                        inputElement.value.value = newValue;
                    }
                });
            }
        });

        // Validation listeners for the input field, active when validateOnValueUpdate is false.
        const validationListeners = {
            blur: evt => {
                value.value = updateMaskedValue(evt);
                return handleBlur(evt, true)
            },
            change: handleChange,
            input: evt => handleChange,
        };

        const attributes = ref(props.attributesString ? JSON.parse(props.attributesString) : {});

        const resetInputValue = () => {
            value.value = '';
            inputElement.value.value = '';
            updateMaskedValue();
            if (props.emitUnmaskedValue) {
                emit('input', '');
            }
            inputElement.value.focus();
            emit('reset');
        };

        const onInputChange = (event) => {
            // When IMask is active, let it handle the input completely
            // Only sync value to vee-validate on blur to avoid cursor position issues
            if (hasMask.value) {
                if (props.emitUnmaskedValue) {
                    return;
                }

                // IMask's native input listener runs after this Vue listener, so
                // read the masked value in a microtask after IMask has updated it.
                queueMicrotask(() => emit('input', getMaskedValue()));
                return;
            }

            value.value = event.target.value;
            emit('input', value.value);
        };

        const onFocus = () => {
            emit('focus');
        };

        const onBlur = () => {
            emit('blur');
        };

        const onInvalid = (event) => {
            value.value = updateMaskedValue(event);
            validate();
        };

        return {
            attributes,
            errorMessage,
            hasValidationError,
            value,
            validationListeners,
            inputElement,
            resetInputValue,
            onInputChange,
            onFocus,
            onBlur,
            onInvalid,
        };
    }
};